import React, { useContext, useEffect, useState } from 'react';
import { TaskExportConfigurationContext } from '../../../../../../../context/TaskExportConfigurationContext';
import TaskExportConfigurationForm, { DraggableTaskExportFieldIdentifier } from '../Common/TaskExportConfigurationForm';
import { useNavigate, useParams } from 'react-router-dom';
import DeleteTaskExportConfigurationModal from './Modals/DeleteTaskExportConfigurationModal';
import { Routing } from '../../../../../../../util/routes.util';
import { useRequest } from '../../../../../../../requests/common/requestHook';
import { TaskExportConfigurationRequest } from '../../../../../../../requests/taskExportConfiguration.request';
import UpdateButton from '../../../../../../components/Button/templates/UpdateButton';
import { BackButton, ButtonVariant, DeleteButton } from '../../../../../../components/Button';
import { DraggableItemData } from '../../../../../Common/DraggableItem';
import {
  TaskExportConfiguration,
  TaskExportConfigurationInput,
  TaskExportField,
} from '../../../../../../../types/task_export_configuration';
import { CompanyContext } from '../../../../../../../context/CompanyContext';

interface UpdateTaskExportConfigurationPaneProps {}

export default function UpdateTaskExportConfigurationPane({}: UpdateTaskExportConfigurationPaneProps) {
  const id = useParams()['id'] || '';
  if (!id) return null;

  const navigate = useNavigate();

  const { currentCompany } = useContext(CompanyContext);
  const { taskExportConfigurations, setTaskExportConfigurations, taskExportConfigurationsLoading } =
    useContext(TaskExportConfigurationContext);

  const existing = taskExportConfigurations.get(id);
  const [input, setInput] = useState<TaskExportConfigurationInput>(
    new TaskExportConfigurationInput(existing || TaskExportConfiguration.default(currentCompany.id)),
  );

  useEffect(() => {
    if (!taskExportConfigurationsLoading && existing) {
      setInput(new TaskExportConfigurationInput(existing));
    }
  }, [taskExportConfigurationsLoading]);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const [update, { loading: updateLoading }] = useRequest(TaskExportConfigurationRequest.update);

  const handleUpdate = async (
    configuration: TaskExportConfigurationInput,
    items: DraggableTaskExportFieldIdentifier[],
  ) => {
    configuration.mapping = DraggableItemData.toMap<TaskExportField>(items);

    const result = await update({ data: [configuration.forUpdate()] });
    if (!result) return;

    const config = result[0];
    taskExportConfigurations.set(config.id, new TaskExportConfiguration(config));
    setTaskExportConfigurations(new Map(taskExportConfigurations));
    navigate(Routing.tasks.settings.report.overview());
  };

  const footer = (configuration: TaskExportConfigurationInput, items: DraggableTaskExportFieldIdentifier[]) => {
    return (
      <div className='flex justify-between'>
        <div>
          <DeleteButton
            disabled={taskExportConfigurationsLoading || updateLoading}
            onClick={() => setOpenDeleteModal(true)}
          />
        </div>
        <div className='flex items-center gap-2'>
          <BackButton
            disabled={updateLoading}
            variant={ButtonVariant.secondary}
            onClick={() => navigate(Routing.tasks.settings.report.overview())}
          />
          <UpdateButton
            loading={updateLoading}
            disabled={taskExportConfigurationsLoading || !configuration.filename || !configuration.name}
            onClick={() => handleUpdate(configuration, items)}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <DeleteTaskExportConfigurationModal open={openDeleteModal} setOpen={setOpenDeleteModal} ids={new Set([id])} />

      <TaskExportConfigurationForm
        input={input}
        setInput={setInput}
        footer={footer}
        loading={taskExportConfigurationsLoading}
      />
    </>
  );
}
